import { environment } from "../../config/environment";
import React, { useCallback, useEffect, useState } from "react";
import { format } from 'date-fns';
import purpleLogo from "../../assets/images/purpleKnowtexLogo.svg";
import { customFetch } from "../../utils/Auth";
import Sidebar from "../Sidebar";

export default function ExternalUsageDisplay({ setAuth, userOrg, setUserOrg }) {
  const [usageLogs, setUsageLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [activeEvents, setActiveEvents] = useState([]);
  const [activeUsername, setActiveUsername] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "eventCount",
    direction: 'desc'
  });

  // Fetch the logs from the API
  useEffect(() => {
    const fetchUsageLogs = async () => {
      try {
        const response = await customFetch(`${environment.apiUrl}/logging/events`, {
          method: "GET",
          headers: {
            token: localStorage.getItem("token")
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch: ${response.status}`);
        }

        const data = await response.json();
        
        // Transform data into a more usable format
        const transformedData = data.map(user => ({
          username: user.username,
          eventCount: user.events?.length || 0,
          events: user.events || [],
          firstActivity: user.events?.length ?
            new Date(Math.min(...user.events.map(event => event.eventTime))) : null,
          lastActivity: user.events?.length ?
            new Date(Math.max(...user.events.map(event => event.eventTime))) : null,
          loginEvents: user.events?.filter(event => event.eventName === 'login').length || 0,
          pageViewEvents: user.events?.filter(event => event.eventName.startsWith('view:')).length || 0
        }));

        setUsageLogs(transformedData);
        setFilteredLogs(transformedData);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching usage logs:", error);
        setIsLoading(false);
      }
    };

    fetchUsageLogs();
  }, []);

  // Handle sorting functionality
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return (
        <svg className="w-4 h-4 inline-block ml-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }

    return sortConfig.direction === 'asc' ? (
      <svg className="w-4 h-4 inline-block ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 inline-block ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  // Filter logs based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredLogs(usageLogs);
      return;
    }

    const filtered = usageLogs.filter(log =>
      log.username.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredLogs(filtered);
  }, [searchTerm, usageLogs]);

  // Sort logs based on sort config
  const sortedLogs = React.useMemo(() => {
    if (!sortConfig.key) return filteredLogs;

    return [...filteredLogs].sort((a, b) => {
      // Handle text fields
      if (sortConfig.key === 'username') {
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle date fields
      if (sortConfig.key === 'firstActivity' || sortConfig.key === 'lastActivity') {
        const aValue = a[sortConfig.key] ? a[sortConfig.key].getTime() : 0;
        const bValue = b[sortConfig.key] ? b[sortConfig.key].getTime() : 0;

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      }

      // Handle numeric fields
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredLogs, sortConfig]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleReset = () => {
    setSearchTerm("");
  };

  const handleUserClick = (events, username) => {
    setActiveUsername(username);
    setActiveEvents(events || []);
    setShowDetails(true);
  };

  const onClose = useCallback(() => {
    setShowDetails(false);
    setActiveEvents([]);
    setActiveUsername("");
  }, []);

  useEffect(() => {
    if (!showDetails) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showDetails, onClose]);

  const formattedDate = (date) => {
    if (!date) return "N/A";
    try {
      return format(date, 'MM/dd/yyyy h:mm a');
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid date";
    }
  };

  // Get recent activity across all users
  const recentActivity = React.useMemo(() => {
    return usageLogs
      .flatMap(log => (log.events || []).map(event => ({
        username: log.username,
        events: log.events,
        ...event
      })))
      .sort((a, b) => b.eventTime - a.eventTime)
      .slice(0, 5);
  }, [usageLogs]);

  // Get most active users
  const mostActiveUsers = React.useMemo(() => {
    return [...usageLogs]
      .sort((a, b) => b.eventCount - a.eventCount)
      .slice(0, 5);
  }, [usageLogs]);

  return (
    <div className="flex min-h-screen bg-white">
      <div className="h-[100dvh]">
        <div className="flex h-[100%] fixed top-0">
          <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
        </div>
      </div>
      <div
        className="w-full h-full flex flex-col gap-2 text-dark-purple font-sans px-4 md:px-6 lg:px-8 pb-8"
        style={{
          marginLeft: "var(--sidebar-width, 250px)",
          width: "calc(100% - var(--sidebar-width, 250px))",
        }}
      >
      <div className="w-full max-w-7xl flex justify-between mx-auto mt-4">
        <img src={purpleLogo} alt="Knowtex Logo" className="h-12" />
        <h2 className="text-2xl font-bold">External Usage Logs</h2>
      </div>
      <div className="w-full max-w-7xl mx-auto mt-6">
        <h1 className="font-sans text-[36px] font-black">User Activity Dashboard</h1>
        <h3 className="font-sans text-[24px] font-semibold tracking-[.1em]">External User Engagement</h3>
      </div>

      <div className="flex flex-col w-full max-w-7xl mx-auto gap-4">
        <div className="flex justify-between items-center w-full">
          <div className="flex gap-4 w-full lg:w-1/2">
            <div className="flex rounded-md bg-white shadow-knowtex-shadow w-full h-[2.5rem] gap-2">
              <svg className="self-center ml-[.4rem] w-6 h-6 text-ktx-navy dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" strokeLinecap="round" strokeWidth="2" d="m21 21-3.5-3.5M17 10a7 7 0 1 1-14 0 7 7 0 0 1 14 0Z" />
              </svg>
              <input
                type="text"
                placeholder="Search by username"
                name="searchTerm"
                className="text-ktx-navy p-[.5rem] w-full italic border-none bg-none rounded-md self-center"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
            <button
              className="font-semibold hover:bg-ktx-navy rounded-md bg-ktx-purple tracking-widest text-white py-[.7rem] h-[2.5rem] px-[3rem] items-center flex justify-center"
              onClick={handleReset}
            >
              Reset
            </button>
          </div>
          <p className="text-[20px] self-center">
            {format(new Date(), 'MMMM dd, yyyy')}
          </p>
        </div>
      </div>

      {/* Main content area */}
      <div className="w-full max-w-7xl mx-auto mt-4 mb-8 flex flex-col gap-4">
        {/* Main table */}
        <div className="w-full relative rounded-md shadow-knowtex-shadow overflow-hidden">
          <div className="max-h-[55dvh] overflow-y-auto">
            <table className="w-full bg-[#FBF9FF]">
              <thead>
                <tr className="py-2 px-4 sticky top-0 text-white z-10 bg-dark-purple">
                  <th
                    onClick={() => handleSort('username')}
                    className="px-4 py-2 text-left cursor-pointer hover:bg-ktx-navy"
                  >
                    Username {getSortIcon('username')}
                  </th>
                  <th
                    onClick={() => handleSort('eventCount')}
                    className="px-4 py-2 text-center cursor-pointer hover:bg-ktx-navy"
                  >
                    Total Events {getSortIcon('eventCount')}
                  </th>
                  <th
                    onClick={() => handleSort('loginEvents')}
                    className="px-4 py-2 text-center cursor-pointer hover:bg-ktx-navy"
                  >
                    Logins {getSortIcon('loginEvents')}
                  </th>
                  <th
                    onClick={() => handleSort('pageViewEvents')}
                    className="px-4 py-2 text-center cursor-pointer hover:bg-ktx-navy"
                  >
                    Page Views {getSortIcon('pageViewEvents')}
                  </th>
                  <th
                    onClick={() => handleSort('lastActivity')}
                    className="px-4 py-2 text-center cursor-pointer hover:bg-ktx-navy"
                  >
                    Last Activity {getSortIcon('lastActivity')}
                  </th>
                  <th
                    onClick={() => handleSort('firstActivity')}
                    className="px-4 py-2 text-center cursor-pointer hover:bg-ktx-navy"
                  >
                    First Activity {getSortIcon('firstActivity')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  [...Array(5)].map((_, index) => (
                    <tr key={`skeleton-${index}`}>
                      <td className="border-b px-4 py-2">
                        <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      </td>
                      <td className="border-b px-4 py-2">
                        <div className="h-4 w-12 mx-auto bg-gray-200 rounded animate-pulse"></div>
                      </td>
                      <td className="border-b px-4 py-2">
                        <div className="h-4 w-12 mx-auto bg-gray-200 rounded animate-pulse"></div>
                      </td>
                      <td className="border-b px-4 py-2">
                        <div className="h-4 w-12 mx-auto bg-gray-200 rounded animate-pulse"></div>
                      </td>
                      <td className="border-b px-4 py-2">
                        <div className="h-4 w-24 mx-auto bg-gray-200 rounded animate-pulse"></div>
                      </td>
                      <td className="border-b px-4 py-2">
                        <div className="h-4 w-24 mx-auto bg-gray-200 rounded animate-pulse"></div>
                      </td>
                    </tr>
                  ))
                ) : (
                  sortedLogs.map((log, index) => (
                    <tr
                      onClick={() => handleUserClick(log.events, log.username)}
                      key={index}
                      className="hover:bg-light-purple cursor-pointer"
                    >
                      <td className="border-b px-4 py-2">{log.username}</td>
                      <td className="border-b px-4 py-2 text-center">{log.eventCount}</td>
                      <td className="border-b px-4 py-2 text-center">{log.loginEvents}</td>
                      <td className="border-b px-4 py-2 text-center">{log.pageViewEvents}</td>
                      <td className="border-b px-4 py-2 text-center">{formattedDate(log.lastActivity)}</td>
                      <td className="border-b px-4 py-2 text-center">{formattedDate(log.firstActivity)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Stats panels */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-md shadow-knowtex-shadow">
            <h3 className="text-lg font-bold mb-2">Most Active Users</h3>
            <div className="bg-[#FBF9FF] p-2 rounded">
              {mostActiveUsers.map((log, i) => (
                <div
                  key={i}
                  className="py-2 border-b last:border-0 flex justify-between hover:bg-light-purple cursor-pointer transition-colors"
                  onClick={() => handleUserClick(log.events, log.username)}
                >
                  <span className="text-ktx-purple font-medium hover:text-ktx-navy">
                    {log.username}
                  </span>
                  <span className="font-semibold">{log.eventCount} events</span>
                </div>
              ))}
              {mostActiveUsers.length === 0 && (
                <div className="py-4 text-center text-gray-500">
                  No user data available
                </div>
              )}
            </div>
          </div>

          <div className="bg-white p-4 rounded-md shadow-knowtex-shadow">
            <h3 className="text-lg font-bold mb-2">Recent Activity</h3>
            <div className="bg-[#FBF9FF] p-2 rounded">
              {recentActivity.map((event, i) => (
                <div key={i} className="py-2 border-b last:border-0 flex justify-between">
                  <span>
                    <span 
                      className="font-semibold text-ktx-purple hover:text-ktx-navy cursor-pointer"
                      onClick={() => handleUserClick(event.events, event.username)}
                    >
                      {event.username}
                    </span>
                    <span className="mx-1">:</span>
                    <span className="text-gray-700">{event.eventName}</span>
                  </span>
                  <span className="text-gray-600">{formattedDate(new Date(event.eventTime))}</span>
                </div>
              ))}
              {recentActivity.length === 0 && (
                <div className="py-4 text-center text-gray-500">
                  No recent activity
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* User detail modal */}
      {showDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg w-4/5 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <div>
                <h2 className="text-xl font-bold text-dark-purple">
                  Event History for {activeUsername}
                </h2>
                <p className="text-gray-600 text-sm">
                  {activeEvents.length} events recorded
                </p>
              </div>
              <button
                onClick={onClose}
                className="bg-transparent text-gray-500 hover:text-gray-800 p-2 rounded-full hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            {/* User Activity Summary */}
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="text-sm text-gray-500">First Activity</div>
                <div className="font-semibold">
                  {activeEvents.length > 0
                    ? formattedDate(new Date(Math.min(...activeEvents.map(e => e.eventTime))))
                    : "N/A"
                  }
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="text-sm text-gray-500">Last Activity</div>
                <div className="font-semibold">
                  {activeEvents.length > 0
                    ? formattedDate(new Date(Math.max(...activeEvents.map(e => e.eventTime))))
                    : "N/A"
                  }
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded-md">
                <div className="text-sm text-gray-500">Activity Breakdown</div>
                <div className="font-medium">
                  <span className="inline-flex items-center mr-3">
                    <span className="w-3 h-3 inline-block bg-green-500 rounded-full mr-1"></span>
                    {activeEvents.filter(e => e.eventName === 'login').length} Logins
                  </span>
                  <span className="inline-flex items-center">
                    <span className="w-3 h-3 inline-block bg-blue-500 rounded-full mr-1"></span>
                    {activeEvents.filter(e => e.eventName.startsWith('view:')).length} Page Views
                  </span>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-dark-purple text-white">
                    <th className="px-4 py-2 text-left">Event</th>
                    <th className="px-4 py-2 text-left">Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {activeEvents.sort((a, b) => b.eventTime - a.eventTime).map((event, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="border px-4 py-2">
                        {event.eventName === 'login' ? (
                          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-sm">
                            Login
                          </span>
                        ) : event.eventName.startsWith('view:') ? (
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
                            {event.eventName.replace('view:', 'Page: ')}
                          </span>
                        ) : (
                          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-sm">
                            {event.eventName}
                          </span>
                        )}
                      </td>
                      <td className="border px-4 py-2">
                        {formattedDate(new Date(event.eventTime))}
                      </td>
                    </tr>
                  ))}
                  {activeEvents.length === 0 && (
                    <tr>
                      <td colSpan="3" className="border px-4 py-8 text-center text-gray-500">
                        No events recorded for this user
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}